/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight, Menu, X as CloseIcon, Plus,
  Users, ShieldCheck, Wallet, FileText, MapPin, Receipt, Smartphone, Calendar,
  Check, X as XMark, Download, Apple, QrCode, BookOpen, Building2,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────── */
/* PRODUCT PILLARS — every line is a real, live module                        */
/* ─────────────────────────────────────────────────────────────────────────── */
const PILLARS = [
  { Icon: Users,       num: "01", title: "Core HR & Org",         body: "Employees, branches, departments, lifecycle, documents. Multi-state India compliance built-in." },
  { Icon: Calendar,    num: "02", title: "Time & Attendance",     body: "Selfie + geofence check-in, shift rosters, leave types, holiday calendars, overtime rules." },
  { Icon: Wallet,      num: "03", title: "Payroll & Compliance",  body: "Auto-PT, PF, ESI, TDS slabs. Per-state CTC rules. Payslip PDF + bulk bank upload." },
  { Icon: FileText,    num: "04", title: "Performance",           body: "Goals, 360° reviews, calibration, probation tracking. Configurable rating scales." },
  { Icon: MapPin,      num: "05", title: "Field Force Geo-Track", body: "DPDP-compliant background GPS, auto-visit detection, anti-fraud, live map, day replay." },
  { Icon: Receipt,     num: "06", title: "Expenses & Travel",     body: "Receipt OCR, per diem rules, multi-level approvals, accountant export. Card-feed ready." },
  { Icon: ShieldCheck, num: "07", title: "Recruitment & BG check",body: "Kanban ATS with AI résumé parsing, magic-link onboarding wizard, background-verification plug-in." },
  { Icon: Building2,   num: "08", title: "Multi-branch & Budget", body: "Per-branch P&L envelopes, country / region tree, finance-aware approval ceilings." },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* DIFF TABLE — SaarOne vs. the usual suspects (no reseller column)           */
/* ─────────────────────────────────────────────────────────────────────────── */
const DIFF_ROWS = [
  ["Multi-branch with per-branch budgets",   "Full",   "Add-on",  "—",   "—"],
  ["Field-force GPS with anti-fraud",        "Full",   "—",       "—",   "—"],
  ["DeepSeek AI résumé + expense parsing",   "Built-in","—",      "—",   "—"],
  ["DPDP-compliant data retention",          "Default","Manual",  "Manual","Manual"],
  ["Auto multi-state payroll (IN)",          "Full",   "Add-on",  "Partial","Add-on"],
  ["Background-verification module",         "Toggle", "Add-on",  "—",   "Add-on"],
  ["Native Android app (BG tracking)",       "v1.0.3", "Webview", "Webview","Native"],
  ["Per-employee MAU pricing",               "₹99",    "₹350+",   "₹200+", "₹400+"],
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* MOBILE APP FEATURE LIST                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */
const MOBILE_FEATURES = [
  "Geofenced selfie check-in",
  "Field force live tracking",
  "Apply leave · approve requests",
  "View payslips · file expenses",
  "Push notifications · OTA updates",
  "Offline-first · syncs on reconnect",
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* FAQ                                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
const FAQS = [
  { q: "How fast can we go live?",
    a: "14 days for a mid-size company — data migration, statutory configuration (PT/PF/ESI/TDS slabs by state), payroll history import, and admin training included. Most customers run their first payroll within 21 days of signing." },
  { q: "Is SaarOne DPDP-compliant?",
    a: "Yes. Field-force GPS pings auto-delete after 90 days. Consent is captured per employee (version-tracked) before any background tracking begins. Mock-location is refused at the API level. Full data-export and right-to-erasure endpoints are on the roadmap." },
  { q: "Which payroll components are pre-built?",
    a: "All Indian statutory components — PF, ESI, PT (state-wise), TDS slabs, gratuity, bonus act, LWF. CTC structures are template-driven; you can override any component at employee level. Payslip PDFs auto-generated with your branding." },
  { q: "Can we customise the approval chains?",
    a: "Yes. Every approval type (leave / expense / loan / asset / onboarding task) has a generic chain engine that walks the manager hierarchy with configurable per-type rules, per-branch budget ceilings, and amount thresholds. No code changes required." },
  { q: "Do you have a Native mobile app?",
    a: "Yes — a real React-Native app (not a webview wrapper). v1.0.3 is live for Android with field-force GPS, geofenced selfie check-in, leave & payslip access, and OTA JavaScript updates. iOS TestFlight is next." },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* PAGE                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */
export default function Landing() {
  useEffect(() => {
    document.documentElement.classList.add("saarone-page");
    return () => document.documentElement.classList.remove("saarone-page");
  }, []);

  return (
    <div className="saarone-marketing" data-testid="landing-root">
      <Nav/>
      <Hero/>
      <TrustStrip/>
      <Pillars/>
      <BranchesSection/>
      <DiffTable/>
      <MobileAppSection/>
      <FAQSection/>
      <FinalCTA/>
      <Footer/>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* NAV                                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const authed = !!user;
  return (
    <header className="saarone-nav sticky top-0 z-50" data-testid="nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo/>
        <nav className="hidden md:flex items-center gap-9 text-sm font-medium" style={{ color: "var(--slate)" }}>
          <a href="#product"   className="hover:text-[color:var(--ink)] transition-colors" data-testid="nav-product">Product</a>
          <a href="#branches"  className="hover:text-[color:var(--ink)] transition-colors" data-testid="nav-branches">Multi-branch</a>
          <a href="#mobile"    className="hover:text-[color:var(--ink)] transition-colors" data-testid="nav-mobile-app">Mobile app</a>
          <a href="#faq"       className="hover:text-[color:var(--ink)] transition-colors" data-testid="nav-faq">FAQ</a>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {authed ? (
            <Link to="/app" className="text-sm font-medium hover:text-[color:var(--ink)] transition-colors" style={{ color: "var(--slate)" }} data-testid="nav-go-to-app">
              Go to app
            </Link>
          ) : (
            <Link to="/login" className="text-sm font-medium hover:text-[color:var(--ink)] transition-colors" style={{ color: "var(--slate)" }} data-testid="nav-signin">
              Sign in
            </Link>
          )}
          <a href="#demo" className="saarone-btn-primary text-sm" data-testid="nav-book-demo">
            Book a demo <ArrowRight size={14}/>
          </a>
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden p-2 -mr-2" style={{ color: "var(--ink)" }} data-testid="nav-mobile-toggle">
          {open ? <CloseIcon size={22}/> : <Menu size={22}/>}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--line)", background: "var(--bg)" }}>
          <div className="px-6 py-4 flex flex-col gap-3 text-sm" data-testid="nav-mobile-menu">
            {[
              ["product", "Product"], ["branches", "Multi-branch"],
              ["mobile", "Mobile app"], ["faq", "FAQ"],
            ].map(([k, label]) => (
              <a key={k} href={`#${k}`} className="py-2 capitalize" style={{ color: "var(--ink)" }} onClick={() => setOpen(false)}>{label}</a>
            ))}
            <Link to={authed ? "/app" : "/login"} className="py-2 font-medium" style={{ color: "var(--blue)" }} onClick={() => setOpen(false)} data-testid="nav-mobile-signin">{authed ? "Go to app" : "Sign in"}</Link>
            <a href="#demo" className="saarone-btn-primary justify-center" onClick={() => setOpen(false)}>Book a demo <ArrowRight size={14}/></a>
          </div>
        </div>
      )}
    </header>
  );
}

function Logo() {
  return (
    <Link to="/" className="font-display" style={{ color: "var(--ink)", fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em" }} data-testid="logo">
      SaarOne
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* HERO — Editorial: A simple truth                                           */
/* ─────────────────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative pt-20 pb-24 sm:pt-28 sm:pb-32" data-testid="hero">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-4xl">
          <div className="eyebrow saarone-rise" style={{ animationDelay: "60ms" }} data-testid="hero-eyebrow">
            A simple truth
          </div>
          <h1
            className="font-display mt-8 leading-[1.02] tracking-tight saarone-rise"
            style={{
              fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
              animationDelay: "120ms",
            }}
            data-testid="hero-h1"
          >
            Your team beat<br/>
            the benchmark.<br/>
            Your HR system<br/>
            still <span style={{ color: "var(--green)" }}>didn't notice.</span>
          </h1>

          <p
            className="mt-10 text-lg sm:text-xl max-w-2xl saarone-rise"
            style={{ color: "var(--slate)", lineHeight: 1.5, animationDelay: "260ms" }}
            data-testid="hero-sub"
          >
            SaarOne is the business operations platform for India's growing companies — payroll,
            attendance, field-force GPS, expenses, recruitment, performance — all in one place.
            Multi-state compliant, mobile-first, and priced like software should be.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3 saarone-rise" style={{ animationDelay: "400ms" }} data-testid="hero-cta-row">
            <a href="#demo" className="saarone-btn-primary text-base px-6 py-3.5" data-testid="hero-cta-primary">
              Book a 30-min demo <ArrowRight size={16}/>
            </a>
            <Link to="/login" className="saarone-btn-ghost text-base px-6 py-3.5" data-testid="hero-cta-secondary">
              Try the live demo
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-3 saarone-rise" style={{ animationDelay: "540ms" }}>
            <span className="saarone-pill" data-testid="hero-pill">
              <span className="dot"/> Trusted by 40+ Indian companies
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRUST STRIP — neutral logos / stats                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
function TrustStrip() {
  const STATS = [
    { k: "5,200+",  v: "Employees managed" },
    { k: "₹46 Cr",  v: "Payroll processed monthly" },
    { k: "120+",    v: "Branches across India" },
    { k: "99.97%",  v: "Uptime — last 12 months" },
  ];
  return (
    <section className="saarone-section-tint py-14" data-testid="trust-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-y-8">
        {STATS.map((s, i) => (
          <div key={i} className="saarone-stat" data-testid={`trust-stat-${i}`}>
            <div className="font-display text-3xl sm:text-4xl" style={{ color: "var(--ink)" }}>{s.k}</div>
            <div className="text-sm mt-1" style={{ color: "var(--slate)" }}>{s.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* PILLARS — the 8 product lines                                              */
/* ─────────────────────────────────────────────────────────────────────────── */
function Pillars() {
  return (
    <section id="product" className="py-24 sm:py-32" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="eyebrow">The platform</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]" data-testid="pillars-h2">
            One license. <span style={{ color: "var(--blue)" }}>Eight</span> modules.<br/>
            Zero copy-paste between them.
          </h2>
          <p className="mt-6 text-lg" style={{ color: "var(--slate)" }} data-testid="pillars-sub">
            Most companies stitch payroll + attendance + expenses + recruitment from four vendors. The
            data never reconciles. SaarOne ships them as one product — same employee record, same
            org tree, same approval engine.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p, i) => (
            <div key={i} className="saarone-card p-6" data-testid={`pillar-${i + 1}`}>
              <div className="flex items-start justify-between mb-5">
                <div
                  className="grid place-items-center rounded-lg"
                  style={{ width: 40, height: 40, background: "var(--blue-tint)" }}
                >
                  <p.Icon size={20} style={{ color: "var(--blue)" }}/>
                </div>
                <div className="font-mono text-xs" style={{ color: "var(--slate-2)" }}>{p.num}</div>
              </div>
              <div className="font-display text-lg mb-2" style={{ color: "var(--ink)" }}>{p.title}</div>
              <div className="text-sm" style={{ color: "var(--slate)", lineHeight: 1.55 }}>{p.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* BRANCHES — multi-branch story (without any reseller layer)                 */
/* ─────────────────────────────────────────────────────────────────────────── */
function BranchesSection() {
  return (
    <section id="branches" className="saarone-section-tint py-24 sm:py-32" data-testid="branches-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow">Multi-branch</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]" data-testid="branches-h2">
            5, 50, or<br/>
            <span style={{ color: "var(--blue)" }}>500 branches</span>.<br/>
            One screen.
          </h2>
          <p className="mt-7 text-lg max-w-md" style={{ color: "var(--slate)" }} data-testid="branches-sub">
            Configure the org tree once — Region → Country → Branch → Department. Every approval chain,
            every budget envelope, every payroll rule inherits down the tree. New branch? It's three
            clicks and a templated rule-set.
          </p>
          <a href="#demo" className="saarone-btn-green mt-9" data-testid="branches-cta">
            See it on a real org <ArrowRight size={16}/>
          </a>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {[
            { h: "Per-branch P&L",          p: "Set ceilings for headcount, expense, and capex per branch. Approvals auto-block once budget is breached.",   Icon: Wallet },
            { h: "Inherited approvals",     p: "Country head → Region → Branch manager — chains walk the hierarchy automatically. No hardcoding.",            Icon: ShieldCheck },
            { h: "Region-scoped reports",   p: "Branch managers see only their unit. CFO sees the whole tree, rolled up — every drilldown is one click.",     Icon: FileText },
            { h: "Multi-state payroll",     p: "Different PT slabs, holiday lists, and shift rules per state — all governed centrally, applied locally.",     Icon: Building2 },
          ].map((c, i) => (
            <div key={i} className="saarone-card p-6" data-testid={`branch-card-${i}`}>
              <div className="grid place-items-center rounded-lg mb-4"
                style={{ width: 38, height: 38, background: "var(--green-tint)" }}>
                <c.Icon size={18} style={{ color: "var(--green)" }}/>
              </div>
              <div className="font-display text-base mb-1.5" style={{ color: "var(--ink)" }}>{c.h}</div>
              <div className="text-sm" style={{ color: "var(--slate)", lineHeight: 1.55 }}>{c.p}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* DIFFERENTIATION TABLE                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */
function DiffTable() {
  return (
    <section className="py-24 sm:py-32" data-testid="diff-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="eyebrow">The honest comparison</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05]" data-testid="diff-h2">
            What's actually included<br/>
            — versus <span style={{ color: "var(--green)" }}>"add-on at quote-time"</span>.
          </h2>
        </div>

        <div className="mt-12 overflow-x-auto">
          <table className="saarone-table w-full" data-testid="diff-table">
            <thead>
              <tr>
                <th className="w-[42%]">Capability</th>
                <th className="col-saarone-head">SaarOne</th>
                <th>Vendor K</th>
                <th>Vendor Z</th>
                <th>Vendor D</th>
              </tr>
            </thead>
            <tbody>
              {DIFF_ROWS.map(([cap, s, ...rest], i) => (
                <tr key={i} data-testid={`diff-row-${i}`}>
                  <td>{cap}</td>
                  <td className="col-saarone font-semibold">
                    <span className="inline-flex items-center gap-1.5">
                      <Check size={14} style={{ color: "var(--green)" }}/> {s}
                    </span>
                  </td>
                  {rest.map((v, j) => (
                    <td key={j} style={{ color: v === "—" ? "var(--slate-2)" : "var(--slate)" }}>
                      {v === "—" ? <XMark size={14}/> : v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 text-xs font-mono" style={{ color: "var(--slate-2)" }}>
          * Comparison based on publicly-published pricing & feature pages as of Feb 2026. Anonymised
          to keep your procurement team happy.
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* MOBILE APP — Smart download button (HEAD probe → real link OR notify-me)   */
/* ─────────────────────────────────────────────────────────────────────────── */
function DownloadApkButton() {
  const [state, setState] = useState("checking"); // checking | ready | pending
  const [showNotify, setShowNotify] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const APK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/mobile-app/android`;

  useEffect(() => {
    let cancelled = false;
    // Use the metadata endpoint — cheap, no APK transfer, also tells us size.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/mobile-app`)
      .then(r => r.json())
      .then(meta => {
        if (cancelled) return;
        setState(meta?.android?.apk_available ? "ready" : "pending");
      })
      .catch(() => !cancelled && setState("pending"));
    return () => { cancelled = true; };
  }, []);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await fetch("/api/mobile/apk-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch { /* keep UX optimistic */ }
    setSent(true);
  };

  if (state === "ready") {
    return (
      <a
        href={APK_URL}
        className="saarone-btn-primary text-base px-6 py-3.5"
        data-testid="mobile-apk-download"
      >
        <Download size={16}/> Download Android APK
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowNotify(true)}
        className="saarone-btn-primary text-base px-6 py-3.5"
        data-testid="mobile-apk-notify-btn"
      >
        <Download size={16}/> {state === "checking" ? "Checking…" : "Notify me when ready"}
      </button>

      {showNotify && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          style={{ background: "rgba(15,23,42,0.45)" }}
          onClick={() => setShowNotify(false)}
          data-testid="mobile-notify-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl p-6 sm:p-8"
            style={{ background: "var(--bg)", border: "1px solid var(--line-2)", boxShadow: "0 24px 60px -20px rgba(15,23,42,0.25)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {!sent ? (
              <>
                <div className="eyebrow">Android beta</div>
                <h3 className="font-display text-2xl mt-3" style={{ color: "var(--ink)" }}>
                  We'll email you the moment it ships.
                </h3>
                <p className="text-sm mt-3" style={{ color: "var(--slate)" }}>
                  The signed v1.0.3 APK is queued on the EAS build pipeline. Drop your email
                  and we'll send you the download link + sideload instructions. No spam, no follow-up sales.
                </p>
                <form onSubmit={handleNotify} className="mt-6 flex flex-col sm:flex-row gap-2">
                  <input
                    type="email" required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="flex-1 px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2"
                    style={{ background: "var(--bg)", border: "1px solid var(--line-2)", color: "var(--ink)" }}
                    data-testid="mobile-notify-email" autoFocus
                  />
                  <button type="submit" className="saarone-btn-primary text-sm px-5" data-testid="mobile-notify-submit">
                    Notify me <ArrowRight size={14}/>
                  </button>
                </form>
                <button type="button" onClick={() => setShowNotify(false)}
                        className="mt-4 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                  Close
                </button>
              </>
            ) : (
              <>
                <div className="eyebrow" style={{ color: "var(--green)" }}>✓ You're on the list</div>
                <h3 className="font-display text-2xl mt-3" style={{ color: "var(--ink)" }}>
                  Thanks — we'll be in touch.
                </h3>
                <p className="text-sm mt-3" style={{ color: "var(--slate)" }}>
                  We'll email <span className="font-mono font-semibold" style={{ color: "var(--ink)" }}>{email}</span> the
                  moment the signed APK is ready. Typically within 24 hours of a build.
                </p>
                <button type="button" onClick={() => { setShowNotify(false); setSent(false); setEmail(""); }}
                        className="mt-6 saarone-btn-ghost text-sm px-5 py-2.5">
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function MobileAppSection() {
  return (
    <section id="mobile" className="relative py-24 sm:py-32" data-testid="mobile-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — copy + CTAs */}
          <div className="lg:col-span-7">
            <div className="eyebrow" data-testid="mobile-eyebrow">Native mobile</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.05]" data-testid="mobile-h2">
              Your team's <span style={{ color: "var(--green)" }}>day</span><br/>
              starts in their pocket.
            </h2>
            <p className="mt-7 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: "var(--slate)" }} data-testid="mobile-sub">
              A real React Native app — not a webview wrapper. Field reps log visits with GPS,
              employees punch in with a selfie, managers approve with a swipe. Background
              location, offline cache, OTA updates. Live on Android today; iOS on TestFlight next.
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="mobile-feature-list">
              {MOBILE_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--ink-soft)" }}>
                  <Check size={16} style={{ color: "var(--green)" }} className="flex-none mt-0.5"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="mobile-cta-row">
              <DownloadApkButton/>
              <a
                href="#mobile-coming-soon"
                className="saarone-btn-ghost text-base px-6 py-3.5 opacity-60 cursor-not-allowed"
                onClick={(e) => e.preventDefault()}
                data-testid="mobile-ios-soon"
                aria-disabled="true"
              >
                <Apple size={16}/> iOS · TestFlight soon
              </a>
            </div>

            <div className="mt-5 text-xs font-mono" style={{ color: "var(--slate-2)" }} data-testid="mobile-apk-meta">
              v1.0.3 · ~70 MB · Android 8.0+ · signed APK (sideload requires "Install unknown apps")
            </div>
          </div>

          {/* Right — phone mockup card (light theme) */}
          <div className="lg:col-span-5 relative" data-testid="mobile-phone-card">
            <div
              className="relative mx-auto"
              style={{
                width: "100%", maxWidth: 320, aspectRatio: "9 / 19",
                borderRadius: 36, padding: 10,
                background: "linear-gradient(150deg, var(--bg-3), var(--bg-2))",
                boxShadow: "0 30px 60px -20px rgba(15,23,42,0.20), 0 0 0 1px var(--line)",
              }}
            >
              <div
                className="w-full h-full flex flex-col"
                style={{
                  borderRadius: 28, overflow: "hidden",
                  background: "var(--bg)",
                  border: "1px solid var(--line)",
                }}
              >
                {/* Status bar */}
                <div className="flex items-center justify-between px-5 py-3 text-[10px] font-mono" style={{ color: "var(--slate-2)" }}>
                  <span>9:41</span>
                  <span>● ● ●</span>
                </div>
                {/* Header */}
                <div className="px-5 pb-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em]" style={{ color: "var(--slate-2)" }}>SaarOne · Field</div>
                  <div className="font-display text-lg mt-1" style={{ color: "var(--ink)" }}>Good morning, Priya</div>
                </div>
                {/* Active day pill */}
                <div className="mx-5 mb-3 rounded-xl px-3.5 py-2.5" style={{ background: "var(--green-tint)", border: "1px solid var(--green)" }}>
                  <div className="text-[10px] font-mono uppercase tracking-wider font-bold" style={{ color: "var(--green-deep)" }}>● Day active</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>3 visits · 12.4 km · since 09:14</div>
                </div>
                {/* Visit list mock */}
                <div className="mx-5 mb-3 space-y-2">
                  {[
                    { t: "ACME Bangalore", st: "10:02", ok: true },
                    { t: "Tata Coffee HQ", st: "10:45", ok: true },
                    { t: "Pune Site Visit", st: "in progress", ok: false },
                  ].map((v, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                         style={{ background: "var(--bg-2)", border: "1px solid var(--line)" }}>
                      <div>
                        <div className="text-[12px]" style={{ color: "var(--ink)" }}>{v.t}</div>
                        <div className="text-[10px] font-mono mt-0.5" style={{ color: "var(--slate)" }}>{v.st}</div>
                      </div>
                      <Check size={12} style={{ color: v.ok ? "var(--green)" : "var(--blue)" }}/>
                    </div>
                  ))}
                </div>
                {/* Bottom CTA */}
                <div className="mt-auto px-5 pb-6">
                  <div className="rounded-lg py-3 text-center text-xs font-semibold"
                       style={{ background: "var(--blue)", color: "#fff" }}>
                    ▶ Log new visit
                  </div>
                  <div className="grid grid-cols-5 mt-4 text-center text-[10px] font-mono" style={{ color: "var(--slate-2)" }}>
                    {["🏠","🕐","📅","🔔","⋯"].map((g, i) => <div key={i}>{g}</div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative QR badge */}
            <div className="hidden lg:flex absolute -right-4 bottom-6 items-center gap-2 px-3 py-2 rounded-lg"
                 style={{ background: "var(--bg)", border: "1px solid var(--line-2)", boxShadow: "0 6px 20px -8px rgba(15,23,42,0.15)" }}>
              <QrCode size={20} style={{ color: "var(--blue)" }}/>
              <div>
                <div className="text-[9px] font-mono uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>Scan to install</div>
                <div className="text-[10px] font-semibold" style={{ color: "var(--ink)" }}>saarone.com/download</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* FAQ                                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
function FAQSection() {
  return (
    <section id="faq" className="saarone-section-tint py-24 sm:py-32" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="eyebrow">FAQ</div>
        <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05]" data-testid="faq-h2">
          The five things every<br/>
          buyer asks in <span style={{ color: "var(--blue)" }}>round one</span>.
        </h2>

        <div className="mt-14 divide-y" style={{ borderColor: "var(--line)" }}>
          {FAQS.map((f, i) => (
            <details key={i} className="saarone-faq py-6" data-testid={`faq-${i}`}>
              <summary className="flex items-start justify-between gap-6 text-lg font-semibold" style={{ color: "var(--ink)" }}>
                <span>{f.q}</span>
                <Plus size={20} className="plus flex-none mt-1"/>
              </summary>
              <p className="mt-4 text-base" style={{ color: "var(--slate)", lineHeight: 1.6 }}>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* FINAL CTA                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */
function FinalCTA() {
  return (
    <section id="demo" className="relative py-28 sm:py-36 text-center" data-testid="final-cta">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="eyebrow">Get started</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.02]" data-testid="cta-h2">
          Run your next payroll<br/>
          on <span style={{ color: "var(--green)" }}>SaarOne</span>.
        </h2>
        <p className="mt-8 text-lg" style={{ color: "var(--slate)" }} data-testid="cta-sub">
          14-day go-live, statutory configuration included, dedicated success manager — flat ₹99 per employee per month.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <a href="mailto:hello@saarone.com?subject=Demo%20request"
             className="saarone-btn-primary text-base px-6 py-3.5"
             data-testid="cta-book-demo">
            Book a 30-min demo <ArrowRight size={16}/>
          </a>
          <Link to="/login" className="saarone-btn-ghost text-base px-6 py-3.5" data-testid="cta-try-demo">
            Try the live demo
          </Link>
        </div>
        <div className="mt-10 flex items-center justify-center gap-3 text-sm font-mono" style={{ color: "var(--slate-2)" }}>
          <BookOpen size={14}/> <a href="/help" className="hover:text-[color:var(--blue)]">Read the docs</a>
          <span>·</span>
          <a href="mailto:hello@saarone.com" className="hover:text-[color:var(--blue)]">hello@saarone.com</a>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* FOOTER                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="border-t" style={{ borderColor: "var(--line)", background: "var(--bg-2)" }} data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-2 sm:grid-cols-4 gap-8">
        <div className="col-span-2">
          <Logo/>
          <p className="mt-4 text-sm max-w-xs" style={{ color: "var(--slate)" }}>
            One business-operations platform for India's growing companies — payroll, attendance,
            field force, expenses, recruitment, performance.
          </p>
        </div>
        {[
          { h: "Product",  items: [["Modules", "#product"], ["Multi-branch", "#branches"], ["Mobile app", "#mobile"], ["FAQ", "#faq"]] },
          { h: "Company",  items: [["Sign in", "/login"], ["Book demo", "#demo"], ["Privacy", "/privacy"], ["Terms", "/terms"]] },
        ].map((col, i) => (
          <div key={i}>
            <div className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: "var(--slate)" }}>{col.h}</div>
            <ul className="space-y-2 text-sm">
              {col.items.map(([label, href], j) => (
                <li key={j}>
                  {href.startsWith("/")
                    ? <Link to={href} className="hover:text-[color:var(--blue)] transition-colors" style={{ color: "var(--ink-soft)" }}>{label}</Link>
                    : <a href={href} className="hover:text-[color:var(--blue)] transition-colors" style={{ color: "var(--ink-soft)" }}>{label}</a>
                  }
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 text-xs font-mono flex flex-wrap items-center justify-between gap-2"
             style={{ color: "var(--slate-2)" }}>
          <div>© 2026 SaarOne · Phonebooth Mobility Solutions Pvt Ltd · Bangalore, India</div>
          <div className="font-script text-sm" style={{ color: "var(--green)" }}>Prospering teams…</div>
        </div>
      </div>
    </footer>
  );
}
